import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Plus, Minus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * Shared visual primitives for the publishing calendar.
 *
 * Extracted from PublishingSchedule.js so the aggregated Publishing Overview
 * panel (Template / Month / Week / Table) can reuse the exact same cell
 * styling, month/week grids, and activity dialog. Keeps both surfaces in
 * lock-step so ops folks see the same visuals whether they open a specific
 * TL's schedule or an aggregate at CM/Department level.
 */

export const DOW_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
export const DOW_LABELS = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };
export const STATUS_STYLE = {
  green:    { bg: "bg-emerald-100 border-emerald-300 text-emerald-800", dot: "bg-emerald-500", label: "Published" },
  red:      { bg: "bg-rose-100 border-rose-300 text-rose-800",           dot: "bg-rose-500",     label: "Missed" },
  yellow:   { bg: "bg-amber-100 border-amber-300 text-amber-800",       dot: "bg-amber-500",    label: "Unscheduled" },
  upcoming: { bg: "bg-slate-100 border-slate-300 text-slate-600",       dot: "bg-slate-400",    label: "Upcoming" },
};

// Local YYYY-MM-DD (avoids UTC-shift bug from `.toISOString()`).
export const toLocalIso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/* --------- Filter chips (shared) --------- */
export function FilterBar({ filter, setFilter, testPrefix = "pub-filter" }) {
  const items = [["none", "All slots"], ["missed_m", "Missed this month"], ["missed_w", "Missed this week"]];
  return (
    <div className="flex items-center gap-1.5 mb-3">
      {items.map(([k, l]) => (
        <button key={k} onClick={() => setFilter(k)} data-testid={`${testPrefix}-${k}`}
          className={`px-2.5 py-1 rounded-full text-xs border transition ${filter === k ? "bg-purple-600 border-purple-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-purple-300 hover:text-purple-600"}`}>
          {l}
        </button>
      ))}
    </div>
  );
}

/* --------- Activity Dialog --------- */
export function ActivityDialog({ slot, onClose }) {
  const style = STATUS_STYLE[slot.status];
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="pub-activity-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="truncate">{slot.project_name}</span>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${style.bg}`}>{style.label}</span>
          </DialogTitle>
          <div className="text-xs text-slate-500 mt-0.5">{slot.date} · {DOW_LABELS[slot.dow]}{slot.schedule_name ? ` · ${slot.schedule_name}` : ""}</div>
        </DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {(slot.articles || []).length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">
              {slot.status === "upcoming" ? "Not yet published — this slot is upcoming." :
               slot.status === "red" ? "No article was published for this project on this day." :
               "No activity."}
            </div>
          ) : (
            (slot.articles || []).map((a) => (
              <div key={a.id} className="border border-slate-200 rounded-md p-3 bg-slate-50/60" data-testid={`pub-activity-article-${a.id}`}>
                <div className="text-sm font-medium text-slate-900 mb-1.5">{a.keyword}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-600">
                  <div><span className="text-slate-400">Writer:</span> <span className="font-medium text-slate-800">{a.writer_name || "—"}</span></div>
                  <div><span className="text-slate-400">Publisher:</span> <span className="font-medium text-slate-800">{a.publisher_name || "—"}</span></div>
                  {a.editor_name && <div><span className="text-slate-400">Editor:</span> <span className="font-medium text-slate-800">{a.editor_name}</span></div>}
                  <div><span className="text-slate-400">Status changed by:</span> <span className="font-medium text-slate-800">{a.status_changed_by || "—"}</span></div>
                  <div className="sm:col-span-2"><span className="text-slate-400">Date:</span> <span className="font-medium text-slate-800">{a.published_at ? new Date(a.published_at).toLocaleString() : "—"}</span></div>
                </div>
                <div className="mt-2 text-xs">
                  <span className="text-slate-400">Post slug:</span>{" "}
                  {a.post_slug ? (
                    /^https?:/i.test(a.post_slug) ? (
                      <a href={a.post_slug} target="_blank" rel="noreferrer" className="text-purple-600 hover:underline break-all">{a.post_slug}</a>
                    ) : (
                      <span className="text-slate-800 break-all">{a.post_slug}</span>
                    )
                  ) : <span className="text-slate-400">—</span>}
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* --------- Month View --------- */
export function MonthView({ slots, onSlotClick }) {
  const [month, setMonth] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  // Iter 36.9.13 — Bug 21. Track which days the caller has expanded to see
  // ALL slots (beyond the initial 3). Key is the ISO date string; value is
  // boolean. Resets naturally when the caller flips months (component keeps
  // remounted state, but that's fine since we only maintain the current
  // month's expansion).
  const [expandedDays, setExpandedDays] = useState({});
  const slotsByDate = useMemo(() => {
    const m = {};
    for (const s of slots) { (m[s.date] = m[s.date] || []).push(s); }
    return m;
  }, [slots]);
  const first = new Date(month.y, month.m, 1);
  const startDow = (first.getDay() + 6) % 7; // Mon=0
  const daysInMonth = new Date(month.y, month.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(month.y, month.m, d));
  const monthLabel = new Date(month.y, month.m, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const shift = (delta) => setMonth((m) => { const d = new Date(m.y, m.m + delta, 1); return { y: d.getFullYear(), m: d.getMonth() }; });
  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-slate-50">
        <button onClick={() => shift(-1)} className="p-1.5 rounded hover:bg-white text-slate-600" data-testid="month-prev"><ChevronLeft className="h-4 w-4" /></button>
        <div className="text-sm font-semibold text-slate-800" data-testid="month-label">{monthLabel}</div>
        <button onClick={() => shift(1)} className="p-1.5 rounded hover:bg-white text-slate-600" data-testid="month-next"><ChevronRight className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((l) => <div key={l} className="px-2 py-1.5 text-center border-r border-slate-200 last:border-r-0">{l}</div>)}
      </div>
      <div className="grid grid-cols-7" style={{ minHeight: 360 }}>
        {cells.map((d, i) => {
          if (!d) return <div key={i} className="border-r border-b border-slate-100 bg-slate-50/40" style={{ minHeight: 96 }} />;
          const iso = toLocalIso(d);
          const daySlots = slotsByDate[iso] || [];
          const today = new Date();
          const isToday = today.toDateString() === d.toDateString();
          const expanded = !!expandedDays[iso];
          const shown = expanded ? daySlots : daySlots.slice(0, 3);
          return (
            <div key={i} className={`border-r border-b border-slate-100 p-1 ${isToday ? "bg-purple-50/40" : ""}`} style={{ minHeight: 96 }} data-testid={`month-day-${iso}`}>
              <div className={`text-[11px] font-medium text-right ${isToday ? "text-purple-700" : "text-slate-400"} mb-0.5`}>{d.getDate()}</div>
              <div className="space-y-0.5">
                {shown.map((s, idx) => (
                  <button key={idx} type="button" onClick={() => onSlotClick?.(s)}
                    className={`w-full text-left text-[10px] px-1 py-0.5 rounded border ${STATUS_STYLE[s.status].bg} truncate hover:brightness-95 transition`}
                    title={`${s.project_name}${s.schedule_name ? " · " + s.schedule_name : ""} — ${STATUS_STYLE[s.status].label}`}
                    data-testid={`month-slot-${s.date}-${s.project_id}-${idx}`}>
                    {s.project_name}
                  </button>
                ))}
                {/* Iter 36.9.13 — Bug 21. "+N more" is now a clickable pill
                    that expands the day cell in-place. Clicking again
                    collapses. */}
                {daySlots.length > 3 && (
                  <button
                    type="button"
                    onClick={() => setExpandedDays((prev) => ({ ...prev, [iso]: !prev[iso] }))}
                    className="text-[10px] font-medium text-purple-600 hover:text-purple-800 hover:underline px-1"
                    data-testid={`month-more-${iso}`}
                  >
                    {expanded ? "Show less" : `+${daySlots.length - 3} more`}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* --------- Week View --------- */
export function WeekView({ slots, onSlotClick }) {
  const [weekStart, setWeekStart] = useState(() => { const d = new Date(); const off = (d.getDay() + 6) % 7; d.setDate(d.getDate() - off); d.setHours(0,0,0,0); return d; });
  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(weekStart.getDate() + i); return d; });
  const slotsByDate = useMemo(() => { const m = {}; for (const s of slots) (m[s.date] = m[s.date] || []).push(s); return m; }, [slots]);
  const shift = (delta) => setWeekStart((d) => { const n = new Date(d); n.setDate(n.getDate() + delta * 7); return n; });
  const label = `${days[0].toLocaleDateString(undefined, { month: "short", day: "numeric" })} — ${days[6].toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-slate-50">
        <button onClick={() => shift(-1)} className="p-1.5 rounded hover:bg-white text-slate-600" data-testid="week-prev"><ChevronLeft className="h-4 w-4" /></button>
        <div className="text-sm font-semibold text-slate-800">{label}</div>
        <button onClick={() => shift(1)} className="p-1.5 rounded hover:bg-white text-slate-600" data-testid="week-next"><ChevronRight className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-7">
        {days.map((d) => {
          const iso = toLocalIso(d);
          const daySlots = slotsByDate[iso] || [];
          return (
            <div key={iso} className="border-r border-slate-100 last:border-r-0 min-h-[280px] p-2">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
              <div className="text-lg font-semibold text-slate-800">{d.getDate()}</div>
              <div className="mt-2 space-y-1">
                {daySlots.map((s, i) => (
                  <button key={i} type="button" onClick={() => onSlotClick?.(s)}
                    className={`w-full text-left text-[11px] px-1.5 py-1 rounded border ${STATUS_STYLE[s.status].bg} hover:brightness-95 transition`}
                    data-testid={`week-slot-${s.date}-${s.project_id}-${i}`}>
                    <div className="font-medium truncate">{s.project_name}</div>
                    <div className="text-[10px] opacity-75">{STATUS_STYLE[s.status].label}{s.schedule_name ? ` · ${s.schedule_name}` : ""}</div>
                  </button>
                ))}
                {daySlots.length === 0 && <div className="text-[11px] text-slate-300 mt-1">—</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* --------- Table View --------- */
export function TableView({ slots, onSlotClick, showSchedule = false }) {
  const sorted = [...slots].sort((a, b) => a.date.localeCompare(b.date));
  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto max-h-[70vh]">
        <table className="w-full text-sm" style={{ minWidth: "max-content" }}>
          <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
            <tr>
              {showSchedule && <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 min-w-[180px]">Schedule</th>}
              <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 min-w-[120px]">Date</th>
              <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 min-w-[60px]">Day</th>
              <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 min-w-[220px]">Project</th>
              <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 min-w-[120px]">Status</th>
              <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Articles</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr><td colSpan={showSchedule ? 6 : 5} className="text-center py-8 text-sm text-slate-400">No publishing slots in this range.</td></tr>
            )}
            {sorted.map((s, i) => (
              <tr key={i} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => onSlotClick?.(s)} data-testid={`table-slot-${s.date}-${s.project_id}-${i}`}>
                {showSchedule && <td className="px-4 py-2 text-slate-700">{s.schedule_name}</td>}
                <td className="px-4 py-2 text-slate-700 font-medium">{s.date}</td>
                <td className="px-4 py-2 text-slate-500">{DOW_LABELS[s.dow]}</td>
                <td className="px-4 py-2 text-slate-800">
                  <button type="button" className="hover:underline hover:text-purple-600 text-left" onClick={(e) => { e.stopPropagation(); onSlotClick?.(s); }}>
                    {s.project_name}
                  </button>
                </td>
                <td className="px-4 py-2">
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium border ${STATUS_STYLE[s.status].bg}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${STATUS_STYLE[s.status].dot}`} />
                    {STATUS_STYLE[s.status].label}
                  </span>
                </td>
                <td className="px-4 py-2 text-slate-600 text-xs">
                  {(s.articles || []).map((a) => a.keyword || a.title || "(untitled)").join(", ") || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* --------- Template Matrix (read-only support for aggregation) --------- */
export function TemplateMatrix({ rows, canEdit, onToggle, onSetCount, groupLabel }) {
  if (!rows.length) {
    return <div className="text-center py-16 text-sm text-slate-400 bg-white rounded-lg border border-slate-200">
      {groupLabel ? `${groupLabel} has no projects yet.` : "This Team Lead has no projects yet. Assign projects to see them here."}
    </div>;
  }
  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      {groupLabel && (
        <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
          {groupLabel}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" style={{ minWidth: "max-content" }}>
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 min-w-[220px]">Project</th>
              {DOW_KEYS.map((d) => (
                <th key={d} className="px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-500 min-w-[80px]">{DOW_LABELS[d]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.project_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`pub-tpl-row-${r.project_id}`}>
                <td className="px-4 py-2 font-medium text-slate-800">{r.project_name}</td>
                {DOW_KEYS.map((d) => {
                  // Iter 45.45 — a cell is now a COUNT (publishes per day).
                  // Checkbox toggles 0/1; "+" adds another publish slot;
                  // "−" (visible when count > 1) removes one.
                  const n = Number(r[d]) || 0;
                  return (
                    <td key={d} className="px-3 py-2 text-center">
                      <div className="inline-flex items-center justify-center gap-1">
                        <input type="checkbox" disabled={!canEdit} checked={n > 0}
                          onChange={(e) => onToggle?.(r.project_id, d, e.target.checked)}
                          className="accent-emerald-600 h-4 w-4 cursor-pointer disabled:cursor-not-allowed"
                          data-testid={`pub-tpl-${r.project_id}-${d}`} />
                        {n > 1 && (
                          <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 border border-emerald-200 rounded px-1"
                            data-testid={`pub-tpl-count-${r.project_id}-${d}`}>×{n}</span>
                        )}
                        {canEdit && (
                          <button type="button" onClick={() => onSetCount?.(r.project_id, d, n + 1)}
                            className="h-4 w-4 inline-flex items-center justify-center rounded-full border border-slate-300 text-slate-400 hover:text-emerald-600 hover:border-emerald-400 hover:bg-emerald-50 transition"
                            title="Add another publish slot for this day"
                            data-testid={`pub-tpl-plus-${r.project_id}-${d}`}>
                            <Plus className="h-2.5 w-2.5" />
                          </button>
                        )}
                        {canEdit && n > 1 && (
                          <button type="button" onClick={() => onSetCount?.(r.project_id, d, n - 1)}
                            className="h-4 w-4 inline-flex items-center justify-center rounded-full border border-slate-300 text-slate-400 hover:text-rose-500 hover:border-rose-300 hover:bg-rose-50 transition"
                            title="Remove one publish slot"
                            data-testid={`pub-tpl-minus-${r.project_id}-${d}`}>
                            <Minus className="h-2.5 w-2.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* --------- Matrix Table View — attendance-style (rows=projects, cols=days) --------- */
export function MatrixTableView({ slots, onSlotClick }) {
  const [monthAnchor, setMonthAnchor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const first = new Date(monthAnchor.y, monthAnchor.m, 1);
  const daysInMonth = new Date(monthAnchor.y, monthAnchor.m + 1, 0).getDate();
  const dates = Array.from({ length: daysInMonth }, (_, i) => new Date(monthAnchor.y, monthAnchor.m, i + 1));
  const projectRow = new Map(); // project_id -> { name, cells: {iso: slot} }
  // Iter 45.46 — multi-publish days emit REPEATED slots; the dot matrix
  // merges them into one cell showing the WORST status (missed stays
  // visible) with all articles combined for the dialog.
  const STATUS_PRI = { red: 3, yellow: 2, upcoming: 1, green: 0 };
  for (const s of slots) {
    const d = new Date(s.date + "T00:00:00");
    if (d.getFullYear() !== monthAnchor.y || d.getMonth() !== monthAnchor.m) continue;
    if (!projectRow.has(s.project_id)) projectRow.set(s.project_id, { name: s.project_name, cells: {} });
    const cells = projectRow.get(s.project_id).cells;
    const cur = cells[s.date];
    if (!cur) {
      cells[s.date] = { ...s, articles: [...(s.articles || [])] };
    } else {
      if ((STATUS_PRI[s.status] || 0) > (STATUS_PRI[cur.status] || 0)) cur.status = s.status;
      cur.articles = [...(cur.articles || []), ...(s.articles || [])];
    }
  }
  const rows = Array.from(projectRow.values()).sort((a, b) => a.name.localeCompare(b.name));
  const monthLabel = first.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const shift = (delta) => setMonthAnchor((m) => { const d = new Date(m.y, m.m + delta, 1); return { y: d.getFullYear(), m: d.getMonth() }; });
  const dayShort = ["S","M","T","W","T","F","S"];
  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-slate-50">
        <button onClick={() => shift(-1)} className="p-1.5 rounded hover:bg-white text-slate-600" data-testid="matrix-prev"><ChevronLeft className="h-4 w-4" /></button>
        <div className="text-sm font-semibold text-slate-800" data-testid="matrix-label">{monthLabel}</div>
        <button onClick={() => shift(1)} className="p-1.5 rounded hover:bg-white text-slate-600" data-testid="matrix-next"><ChevronRight className="h-4 w-4" /></button>
      </div>
      <div className="overflow-x-auto">
        <table className="text-sm" style={{ minWidth: "max-content" }}>
          <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
            <tr>
              <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 min-w-[200px] sticky left-0 bg-slate-50 z-10">Project</th>
              {dates.map((d) => (
                <th key={d.getDate()} className="px-2 py-1 text-center text-[10px] font-semibold text-slate-500 min-w-[36px] border-l border-slate-200">
                  <div>{d.getDate()}</div>
                  <div className="text-slate-400">{dayShort[d.getDay()]}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={dates.length + 1} className="text-center py-8 text-sm text-slate-400">No publishing slots in this month.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.name} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-1 font-medium text-slate-800 sticky left-0 bg-white border-r border-slate-200">{r.name}</td>
                {dates.map((d) => {
                  const key = toLocalIso(d);
                  const s = r.cells[key];
                  if (!s) return <td key={key} className="border-l border-slate-100" />;
                  const st = STATUS_STYLE[s.status];
                  return (
                    <td key={key} className="border-l border-slate-100 text-center p-1">
                      <button type="button" onClick={() => onSlotClick?.(s)}
                        className={`w-6 h-6 rounded-full ${st.dot} hover:scale-110 transition inline-block`}
                        title={`${r.name} — ${key} — ${st.label}${s.schedule_name ? " · " + s.schedule_name : ""}`}
                        data-testid={`matrix-cell-${s.date}-${s.project_id}`} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* --------- Legend row --------- */
export function LegendRow() {
  return (
    <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-slate-500">
      <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Published on schedule</div>
      <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-rose-500" /> Missed (scheduled)</div>
      <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> Unscheduled</div>
      <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-slate-400" /> Upcoming (future)</div>
    </div>
  );
}
